// Global
import { GetStaticComponentProps, Item } from '@sitecore-jss/sitecore-jss-nextjs';
import graphQLClientFactory from 'lib/graphql-client-factory';
import React, { useEffect, useState } from 'react';

// Lib
import { ComponentProps } from 'lib/component-props';
import { DartComponents } from 'lib/templates/Feature.Dart.model';

// Local
import HeaderDesktop from './HeaderDesktop';
import HeaderMobile from './HeaderMobile';
import HeaderQuery from './Header.graphql';
import {
  ActiveCountry,
  countryQuery,
  FormulaIngredients,
  query,
  Result,
} from '../ProductInformation/ProductInformation';

export type HeaderGQLinks = ComponentProps &
  DartComponents.Header &
  DartComponents.HeaderJson & {
    staticProps?: {
      header?: DartComponents.HeaderJson;
    };
  } & { registeredResult?: Result };

const Header = (staticProps: HeaderGQLinks): JSX.Element => {
  const [windowSize, setWindowSize] = useState<number>();

  useEffect(() => {
    // set the window size
    if (typeof window !== 'undefined') {
      setWindowSize(window.innerWidth);
    }

    const handleResize = () => {
      setWindowSize(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [windowSize]);

  return (
    <>
      <HeaderDesktop {...staticProps} />
      <HeaderMobile {...staticProps} />
    </>
  );
};

export default Header;

export const getStaticProps: GetStaticComponentProps = async (rendering, layoutData) => {
  const graphQLClient = graphQLClientFactory({});
  const result = await graphQLClient.request<unknown>(HeaderQuery, {
    datasource: rendering.dataSource,
    params: rendering.params,
    language: layoutData?.sitecore?.context?.language,
  });

  const site = layoutData?.sitecore?.context?.site?.name;
  if (site?.toLocaleLowerCase() === 'wiscj') {
    const formula =
      layoutData?.sitecore?.route?.fields?.productInformation &&
      'fields' in layoutData?.sitecore?.route?.fields?.productInformation
        ? (layoutData?.sitecore?.route?.fields?.productInformation?.fields?.formula as Item[])
        : null;
    const formulaId = formula && formula.length > 0 ? formula[0]?.id : '';
    const language = layoutData?.sitecore?.context?.language;

    const data = await graphQLClient.request<FormulaIngredients>(query, {
      path: formulaId,
      language: language,
    });

    const formulaIngredients = data && data?.item?.children?.results;
    const registeredResult: Result = {
      isRegistered: false,
      wiscj_brand_pdp_page: null,
      wiscj_pdf_url: null,
      wiscj_brand_name: null,
    };
    if (formulaIngredients && formulaIngredients.length > 0) {
      for (const formulaIngredient of formulaIngredients) {
        const activeCountries = formulaIngredient?.activeIngredientCountryMapping?.targetItems;
        if (activeCountries && activeCountries.length > 0) {
          for (const country of activeCountries) {
            const countryData = await graphQLClient.request<ActiveCountry>(countryQuery, {
              path: country?.id,
              language: 'en',
            });
            const languageItems = countryData?.item?.languageList?.targetItems;
            for (const lang of languageItems) {
              if (language?.toLocaleLowerCase() === lang?.name?.toString().toLocaleLowerCase()) {
                registeredResult.isRegistered = true;
                break;
              }
            }
          }
        }
      }
    }
    return {
      staticProps: result,
      registeredResult: registeredResult,
    };
  }

  return {
    staticProps: result,
    registeredResult: null,
  };
};
